import { mat4, quat, vec2, vec3 } from "gl-matrix";
import parseMTL from "./MaterialParser";
import { OBJLoader2 } from "wwobjloader2";
import { MeshData } from "./MeshManager";


function createSegmented(size: number, segments: number) {
  let vertices: number[] = [];
  let triangles: number[] = [];
  let normals: number[] = [];
  let uvs: number[] = [];
  let currentIndex = 0;
  let step = size / segments;

  for (let i = 0; i < size; i += step) {
    for (let j = 0; j < size; j += step) {
      vertices.push(j, 0, i, j + step, 0, i, j, 0, i + step);
      vertices.push(j, 0, i + step, j + step, 0, i, j + step, 0, i + step);

      triangles.push(
        0 + currentIndex,
        2 + currentIndex,
        1 + currentIndex,
        3 + currentIndex,
        5 + currentIndex,
        4 + currentIndex,
      );

      currentIndex += 6;

      for (let k = 0; k < 6; k++) {
        normals.push(0.0, 1.0, 0.0);
      }

      uvs.push(
        j, i,
        j + step, i,
        j, i + step,
        j, i + step,
        j + step, i,
        j + step, i + step,
      );
    }
  }

  return { vertices, triangles, normals, uvs };
}

function loadJSONFile<T = any>(
  cb: (data: T) => void,
  filePath: string,
): void {
  fetch(filePath)
    .then((res) => res.json())
    .then(cb)
    .catch(console.error);
}

async function fetchOBJFile(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch OBJ file: ${response.status}`);
  }
  return response.text();
}

function componentToHex(c: number): string {
  const hex = Math.floor(c * 255).toString(16).padStart(2, "0");
  return hex;
}

function rgbToHex(rgb: vec3): string {
  return (
    "#" +
    componentToHex(rgb[0]) +
    componentToHex(rgb[1]) +
    componentToHex(rgb[2])
  );
}

function hexToRGB(hex: string): vec3 {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return vec3.fromValues(r / 255, g / 255, b / 255);
}

function mat4ToArray(mat: mat4): number[] {
  return Array.from(mat);
}

function initShaderUniforms(
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  uniforms: string[],
  attribs: string[],
) {
  const programInfo: any = {
    attribLocations: {},
    uniformLocations: {},
    program: shaderProgram,
  };

  attribs.forEach(
    (attrib) =>
    (programInfo.attribLocations[attrib] = gl.getAttribLocation(
      shaderProgram,
      attrib,
    )),
  );

  uniforms.forEach(
    (uniform) =>
    (programInfo.uniformLocations[uniform] = gl.getUniformLocation(
      shaderProgram,
      uniform,
    )),
  );

  return programInfo;
}

function getVertexRowN(vertices: number[], n: number): vec3 {
  return vec3.fromValues(
    vertices[n * 3],
    vertices[n * 3 + 1],
    vertices[n * 3 + 2],
  );
}

function getUVRowN(uvs: number[], n: number): vec2 {
  return vec2.fromValues(uvs[n * 2], uvs[n * 2 + 1]);
}

function toRadians(angle: number): number {
  return angle * (Math.PI / 180);
}

function initDepthMap(gl: WebGLRenderingContext, width: number, height: number) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.DEPTH_COMPONENT,
    width,
    height,
    0,
    gl.DEPTH_COMPONENT,
    gl.UNSIGNED_INT,
    null,
  );

  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const depthMapFBO = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, depthMapFBO);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, texture, 0);

  return { depthMapFBO, texture };
}

function vectorDistance(vec1: vec3, vec2: vec3): number {
  return Math.sqrt(
    (vec2[0] - vec1[0]) ** 2 +
    (vec2[1] - vec1[1]) ** 2 +
    (vec2[2] - vec1[2]) ** 2,
  );
}

function quatToEuler(q: quat): [number, number, number] {
  const m = mat4.create();
  mat4.fromQuat(m, q);

  let x: number, y: number, z: number;

  // Assuming intrinsic Tait–Bryan angles order: roll (X), pitch (Y), yaw (Z)
  y = Math.asin(-m[2]); // m[2] = -sin(pitch)

  if (Math.abs(m[2]) < 0.999999) {
    x = Math.atan2(m[6], m[10]);  // atan2(m21, m22)
    z = Math.atan2(m[1], m[0]);   // atan2(m01, m00)
  } else {
    // Gimbal lock case
    x = Math.atan2(-m[9], m[5]);  // atan2(-m12, m11)
    z = 0;
  }

  return [x, y, z];
}

async function captureWebGL2Canvas(canvas: HTMLCanvasElement, gl: WebGL2RenderingContext, sceneId: string): Promise<File> {
  const width = canvas.width;
  const height = canvas.height;

  // Create a temporary framebuffer and attach a texture to copy the screen
  const framebuffer = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

  // Create a texture to hold the pixels
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  // Copy the screen into the framebuffer
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null); // read from default framebuffer (screen)
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, framebuffer); // draw into our temp FBO
  gl.blitFramebuffer(
    0, 0, width, height,
    0, 0, width, height,
    gl.COLOR_BUFFER_BIT,
    gl.NEAREST
  );

  // Read pixels from our temp framebuffer
  const pixels = new Uint8Array(width * height * 4);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

  // Flip vertically
  const flipped = new Uint8ClampedArray(pixels.length);
  const rowBytes = width * 4;
  for (let y = 0; y < height; y++) {
    const src = y * rowBytes;
    const dest = (height - y - 1) * rowBytes;
    flipped.set(pixels.subarray(src, src + rowBytes), dest);
  }

  // Draw into a 2D canvas
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = width;
  tempCanvas.height = height;
  const ctx = tempCanvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2D context");

  ctx.putImageData(new ImageData(flipped, width, height), 0, 0);

  // Convert to File
  const dataURL = tempCanvas.toDataURL("image/png");
  const blob = await (await fetch(dataURL)).blob();
  return new File([blob], `${sceneId}-${Math.floor(Date.now() / 1000)}.png`, { type: "image/png" });
}

async function loadOBJ(text: string): Promise<MeshData> {
  const meshLoader = new OBJLoader2();
  const parseData = meshLoader.parse(text);

  if (!parseData.children || parseData.children.length <= 0) {
    throw new Error("no children parsed");
  }

  const child = parseData.children[0];
  const meshData: MeshData = {
    vertices: child.geometry.attributes.position
      ? child.geometry.attributes.position.array
      : [],
    normals: child.geometry.attributes.normal
      ? child.geometry.attributes.normal.array
      : [],
    uvs: child.geometry.attributes.uv ? child.geometry.attributes.uv.array : [],
  };

  return meshData;
}


const Common = {
  componentToHex,
  getUVRowN,
  getVertexRowN,
  hexToRGB,
  initDepthMap,
  initShaderUniforms,
  loadJSONFile,
  mat4ToArray,
  loadOBJ,
  rgbToHex,
  toRadians,
  vectorDistance,
  createSegmented,
  quatToEuler,
  fetchOBJFile,
  captureWebGL2Canvas,
};

export default Common;
